import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../utils/utils';

/**
 * The app's one card surface.
 *
 * **`dark:bg-card` is not redundant with `bg-card/70`.** The `/70` alpha is a
 * light-mode effect: composited over the page ground it reads as a lifted
 * translucent pane. In dark, `--card` sits close enough to `--ground` that the
 * same alpha dissolves the card into the page and the border is left carrying
 * the whole surface. Every hand-rolled copy of this recipe in `apps/web` had
 * already discovered that and appended `dark:bg-card`; the primitive had not,
 * which is the one real difference between them and why migrating call sites
 * to `Card` without this would have been a visible regression in dark mode.
 *
 * **Elevation is a variant, not a class you pass.** Tailwind's stock `shadow`
 * is a black shadow, which paints nothing against dark's near-black ground —
 * the exact failure `--shadow-elevation-*` exists to fix, and the reason
 * `AuthCard` had to override it. The tokens resolve to a tinted shadow in light
 * and a lit top edge in dark, so one class is right in both themes.
 */
const cardVariants = cva(
  'text-card-foreground border-border/60 bg-card/70 rounded-xl border backdrop-blur-sm dark:bg-card',
  {
    variants: {
      elevation: {
        /** Flat. For cards in a grid, where per-card shadows read as noise. */
        none: '',
        /** The default lift. */
        sm: 'shadow-elevation-1',
        /** A card that floats above the page — auth, modals, the wizard. */
        md: 'shadow-elevation-2',
      },
    },
    defaultVariants: { elevation: 'sm' },
  }
);

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, elevation, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ elevation, className }))}
      {...props}
    />
  )
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5 p-6', className)}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('leading-none font-semibold tracking-tight', className)}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-muted-foreground text-sm', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
));
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center p-6 pt-0', className)}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

export {
  Card,
  cardVariants,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
};
